/**
 * STORYBOARD UI - Drag & Drop Module
 * Handles drag and drop for scene frame slots
 *
 * Extracted from ui.js as part of Phase 2 refactoring
 */

import {
  getActiveStoryboard,
  setSceneFrame
} from '../state.js';

// ============================================================================
// MODULE STATE
// ============================================================================

// Callback for rendering storyboard (set by main ui.js)
let renderStoryboardCallback = null;

/**
 * Set the callback for rendering storyboard
 * Called by main ui.js during initialization
 */
export function setRenderStoryboardCallback(callback) {
  renderStoryboardCallback = callback;
}

// ============================================================================
// DRAG & DROP HANDLERS
// ============================================================================

/**
 * Handle dragover on frame slots
 */
export function handleFrameDragOver(e) {
  const slot = e.target.closest('.frame-slot');
  if (!slot) return;

  e.preventDefault();
  slot.classList.add('drag-over');
}

/**
 * Handle dragleave on frame slots
 */
export function handleFrameDragLeave(e) {
  const slot = e.target.closest('.frame-slot');
  if (!slot) return;

  slot.classList.remove('drag-over');
}

/**
 * Handle drop on frame slots
 */
export function handleFrameDrop(e) {
  const slot = e.target.closest('.frame-slot');
  if (!slot) return;

  e.preventDefault();
  slot.classList.remove('drag-over');

  const files = e.dataTransfer?.files;
  if (files?.length > 0 && files[0].type.startsWith('image/')) {
    handleLocalImageUpload(files[0], slot);
  }
}

/**
 * Handle local image upload (from drop or file input)
 * Uploads to R2 and stores URL (not Base64) to avoid sessionStorage quota issues
 */
export async function handleLocalImageUpload(file, slotElement) {
  const sceneSeq = parseInt(slotElement.dataset.scene);
  const frameType = slotElement.dataset.type;

  if (!sceneSeq || !frameType) {
    console.warn('[Storyboard] Invalid slot element for upload');
    return;
  }

  // Show uploading state
  const contentEl = slotElement.querySelector('.frame-content');
  const originalContent = contentEl?.innerHTML;
  if (contentEl) {
    contentEl.innerHTML = `
      <div class="frame-generating">
        <div class="frame-spinner"></div>
        <span>Uploading...</span>
      </div>
    `;
  }

  try {
    // Upload to R2 via backend
    const formData = new FormData();
    formData.append('image', file);

    const baseUrl = window.API_CONFIG?.baseUrl || '';
    const response = await fetch(`${baseUrl}/api/upload`, {
      method: 'POST',
      body: formData
      // Note: Don't set Content-Type header - browser sets it with boundary
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Upload failed');
    }

    // Store the R2 URL (small string, safe for sessionStorage)
    const imageUrl = data.url;

    // Update state with URL
    setSceneFrame(sceneSeq, frameType, imageUrl, 'upload');

    // Refresh UI
    const storyboard = getActiveStoryboard();
    const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;
    if (renderStoryboardCallback) {
      renderStoryboardCallback(ideaId);
    }

    console.log(`[Storyboard] Uploaded to R2: ${imageUrl}`);

  } catch (error) {
    console.error('[Storyboard] Upload failed:', error);

    // Restore original content on error
    if (contentEl && originalContent) {
      contentEl.innerHTML = originalContent;
    }

    // Show error to user
    alert(`Upload failed: ${error.message}`);
  }
}
